import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  getPhoenixApiTenantUrl,
  modify,
  superFetch,
  defaultHeaders,
} from '@pflegenavi/shared-frontend/platform';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type { ShowResidentDto } from '@pflegenavi/shared/api';

export interface RecurringItemsChargeParams {
  params: {
    residentId: string;
  };
  body: {
    end_date?: string;
    data: Array<{
      recurring_item_id: string;
      charge_full_period: boolean;
    }>;
  };
}

export interface ExitResidentParams {
  params: {
    residentId: string;
  };
  body: {
    exit_date: Date;
    exit_reason: 'deceased' | 'move_out';
  };
}

export interface DeceasedFundsPayoutProcessDto {
  id: string;
  resident_id: string;
  nursing_home_id?: string;
  legal_entity_id?: string;
  status: string;
  transfer_amount?: number;
  transfer_id?: string;
  payout_amount?: number;
  payout_id?: string;
  error_message?: string;
  inserted_at: string;
  updated_at: string;
}

export interface InitiateDeceasedFundsPayoutParams {
  params: {
    residentId: string;
  };
  body?: {
    note?: string;
  };
}

export interface GetDeceasedFundsPayoutProcessesParams {
  params: {
    residentId: string;
  };
}

export interface ArchiveResidentParams {
  params: {
    residentId: string;
  };
  body?: {
    notes?: string;
  };
}

export interface ISettlementPhoenixApi extends Api {
  recurringItemsCharge(dataIn: RecurringItemsChargeParams): Promise<void>;
  exitResident(dataIn: ExitResidentParams): Promise<ShowResidentDto>;
  archiveResident(dataIn: ArchiveResidentParams): Promise<ShowResidentDto>;
  initiateDeceasedFundsPayout(
    dataIn: InitiateDeceasedFundsPayoutParams
  ): Promise<DeceasedFundsPayoutProcessDto>;
  getDeceasedFundsPayoutProcesses(
    dataIn: GetDeceasedFundsPayoutProcessesParams
  ): Promise<{ data: DeceasedFundsPayoutProcessDto[] }>;
}

export class SettlementPhoenixApi implements ISettlementPhoenixApi {
  baseUrl: string;
  headers: Headers;

  recurringItemsCharge: ISettlementPhoenixApi['recurringItemsCharge'];
  exitResident: ISettlementPhoenixApi['exitResident'];
  archiveResident: ISettlementPhoenixApi['archiveResident'];
  initiateDeceasedFundsPayout: ISettlementPhoenixApi['initiateDeceasedFundsPayout'];
  getDeceasedFundsPayoutProcesses: ISettlementPhoenixApi['getDeceasedFundsPayoutProcesses'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getPhoenixApiTenantUrl(tenantId, apiUrl);
    this.headers = defaultHeaders();

    this.recurringItemsCharge = modify({
      authContext,
      url: ({ residentId }) =>
        `${this.baseUrl}/settlement/${residentId}/recurring_items_charge`,
      method: 'PUT',
    });

    this.exitResident = modify({
      authContext,
      url: ({ residentId }) =>
        `${this.baseUrl}/resident/${residentId}/mangopay/settlement/exit`,
      method: 'POST',
    });

    this.archiveResident = modify({
      authContext,
      url: ({ residentId }) =>
        `${this.baseUrl}/resident/${residentId}/mangopay/settlement/archive`,
      method: 'POST',
    });

    this.initiateDeceasedFundsPayout = modify({
      authContext,
      url: ({ residentId }) =>
        `${this.baseUrl}/resident/${residentId}/mangopay/settlement/deceased_funds_payout`,
      method: 'POST',
    });

    this.getDeceasedFundsPayoutProcesses = async ({ params }) => {
      const result = await superFetch(
        this.authContext,
        `${this.baseUrl}/resident/${params.residentId}/mangopay/settlement/deceased_funds_payout_process`,
        {
          method: 'GET',
          headers: this.headers,
        }
      );
      return result.json();
    };
  }
}
